import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import KeywordInputGroupContainer from '../KeywordInputGroup/KeywordInputGroupContainer';
import { LocationGroup } from '../LocationGroup';
import { FacetDropdown } from '../FacetDropdown';
import { AgeRange } from '../AgeRange';
import { Checkbox } from 'module-tsl-ui-elements';
import ReactTooltip from 'react-tooltip';

const TopFilters = ({ history: { push }, base, checkSubmit, facets, pathname, clearFilters, keywords, toggleShowMoreVisible, showMoreVisible, searchType, employerId,
  selectedFacets, changeQuickFilterValue, changeCheckBoxFilterValue, metadata, isMobile }) => {
  
  const { count: jobCount } = metadata || { count: 0 };
  const isSupply = searchType === 'supply';
  const isEmbed = searchType === 'embed';
  const isInternalEmbed = isEmbed && !employerId;

  const quickApplyValue = !!selectedFacets.find( a => a.name === 'applicationType' );
  const FullTimeValue = selectedFacets.find( a => a.name === 'contractTypes' ) && selectedFacets.find( a => a.name === 'contractTypes' ).value && selectedFacets.find( a => a.name === 'contractTypes' ).value.indexOf('Full Time') > -1;
  const PartTimeValue = selectedFacets.find( a => a.name === 'contractTypes' ) && selectedFacets.find( a => a.name === 'contractTypes' ).value && selectedFacets.find( a => a.name === 'contractTypes' ).value.indexOf('Part Time') > -1;
  const NQTTimeValue = selectedFacets.find( a => a.name === 'experiences' ) && selectedFacets.find( a => a.name === 'experiences' ).value && selectedFacets.find( a => a.name === 'experiences' ).value.indexOf('Suitable for NQTs (ECTs)') > -1;
  const excludedLocations = selectedFacets.find( facet => facet.name === 'excludedLocations' );

// facetItemPaths, can look like "l5:Europe:United Kingdom:London"
// however, for some reason, when you send the full facetItemPath, including "l5" to the search endpoint
// it doesn't work.
// Hence, we remove any instance of l[0-9]:
  const locations = facets.locations
    ? facets.locations.map(({ facetItemPath, ...rest }) => ({ ...rest, facetItemPath: facetItemPath.replace(/^l[0-9]:/, '') }))
    : [];

  // This filter function is effectively removing invalid excludedLocations.
  //
  // Let's say, for your location, you've selected "London", the facetItemPath for this location is something like:
  // "l5:Europe:United Kingdom:London"
  // 
  // Now, let's say you've selected "London, City of" as a "sub location" to exclude, the facetItemPath, or in this case, "excludedLocation"
  // would look something like:
  // "l5:Europe:United Kingdom:London:London, City of"
  // 
  // This is a VALID excludedLocation as it sits under the London location. Now take somewhere like Salford, with a faceItemPath of
  // "l5:Europe:United Kingdom:Greater Manchester:Salford"
  // 
  // This would be an INVALID excludedLocation, as it does not sit in the "London hierarchy".
  // 
  // So this filter, if the location was "l5:Europe:United Kingdom:London" and the excluded location was "l5:Europe:United Kingdom:Greater Manchester:Salford", it will find
  // no matches
  const excludedLocationFilter = excludedLocation => locations.find(location => location.facetItemPath === excludedLocation);
  
  // This value is important so we can accurately display how many real locations have been selected
  const effectiveExcludedCount = excludedLocations
    ? excludedLocations.value.filter(excludedLocationFilter).length
    : 0;

  // Now we can work out how many locations are actually being included.
  const selectedCount = locations.length - effectiveExcludedCount;

  // The "edit locations" button should only be visible if there are locations to filter by
  // AND
  // there are either jobs remaining OR there are locations that are being excluded (i.e as you may want to re-select that option)
  const showDropdownTitle = locations.length > 1 && (jobCount > 0 || effectiveExcludedCount > 0);

  const supplyHead = (<h2 className="heading heading--xxl margin-bottom-md">
    {jobCount} Supply teacher jobs
  </h2>);

  const keywordGroup = (<div className="row">
    <h3 className="sr-only">Keywords</h3>
    <div className="col-md-4 padding-right-none">
      <KeywordInputGroupContainer isMobile={isMobile} submit={evt => checkSubmit(evt, push, base, keywords)}/>
    </div>
    <h3 className="sr-only">UK postcode or current location</h3>
    <div className="col-md-8 tds-location">
     <LocationGroup
        placeholder={`${locations.length} locations selected.`}
        showDropdownTitle={showDropdownTitle}
        selectedCount={selectedCount}
        includedTowns={locations}
      />
    </div>
  </div>);

  const hr = <div className="hr"></div>;

  const threeFacets = (<div className="three-facets">
    <div className="row">
      <div className="col-md-4 margin-bottom-xs padding-right-none">
        <div className="ms">
          <FacetDropdown
            placeholder="Position"
            fieldName="positions"
            groupLabel="Positions"
            labelKey="label"
            clearable={false}
            values={(facets.positions || [])} />
        </div>
      </div>
      <div className="col-md-4 margin-bottom-xs">
        <div className="ms">
          <FacetDropdown
            placeholder="Workplaces"
            fieldName="workplaces"
            groupLabel="Workplaces"
            clearable={false}
            values={(facets.workplaces || [])} />
        </div>
      </div>
      <div className="col-md-4 margin-bottom-xs padding-left-none">
        <div className="ms">
          <FacetDropdown
            placeholder="Subject"
            fieldName="subjects"
            groupLabel="Subjects"
            clearable={false}
            values={(facets.subjects || [])} />
        </div>
      </div>
    </div>
  </div>);

  const showMoreButtons = [
    isEmbed ? <div className={isInternalEmbed ? '' : 'powered-by-tes'}></div> : null,
    <div className={`${isEmbed ? 'embed-filter-buttons' : 'clearfix middle-buttons filter-buttons'}`}>
      &nbsp;
      &nbsp;
      &nbsp;
      <a className="link link--colored facets__heading-main t-clear-filters padding-right-md"
        onClick={() => toggleShowMoreVisible(push, pathname)} >
        { showMoreVisible ? 'Fewer filters' : 'More filters' }
      </a>
      { isInternalEmbed
        ? <a className="link link--colored facets__heading-main t-clear-filters"
            href="/jobs/search" >
            All jobs
          </a>
        : <a className="link link--colored facets__heading-main t-clear-filters"
            onClick={() => {
              clearFilters(push, pathname);
            }}
          >
            Clear filters
          </a>
      }
    </div>
  ];

  const showMore = [
    isEmbed
      ? <div className="clearfix">{showMoreButtons}</div>
      : showMoreButtons,
    showMoreVisible && <div className="more-filters">
      <div className="facet-selector">
        { !isSupply && <div className="row padding-right-md margin-bottom-md">
          <div className="col-md-6 padding-right-none margin-bottom-xs">
            <label className="dropdown-label">Contract term</label>
            <div className="ms" data-tip data-for="contractTerms">
              <FacetDropdown
                placeholder="Choose"
                fieldName="contractTerms"
                groupLabel="Contract term"
                clearable={false}
                values={(facets.contractTerms || [])} />
            </div>
          </div>
          <AgeRange />
        </div> }
        <div className="row padding-right-md">
          <div className="col-md-2 col-sm-4 col-xs-6 padding-right-none">
            <Checkbox
              id="FullTimeCheckBox"
              caption="toggle"
              checked={FullTimeValue}
              onChange={(e) => changeCheckBoxFilterValue(e.target.checked, { facets, keywords, base, push, key: 'contractTypes', value: 'Full Time' })}
            />
            <label htmlFor="FullTimeCheckBox" className="checkbox-label">Full time jobs</label>
          </div>
          <div className="col-md-2 col-sm-4 col-xs-6 padding-none">
            <Checkbox
              id="PartTimeCheckBox"
              caption="toggle"
              checked={PartTimeValue}
              onChange={(e) => changeCheckBoxFilterValue(e.target.checked, { facets, keywords, base, push, key: 'contractTypes', value: 'Part Time' })}
            />
            <label htmlFor="PartTimeCheckBox" className="checkbox-label">Part time jobs</label>
          </div>
          <div className={`${isSupply ? 'col-md-2' : 'col-md-3'} col-sm-4 col-xs-6 padding-right-none`}>
            <Checkbox
              id="NQTCheckBox"
              caption="toggle"
              checked={NQTTimeValue}
              onChange={(e) => changeCheckBoxFilterValue(e.target.checked, { facets, keywords, base, push, key: 'experiences', value: 'Suitable for NQTs (ECTs)' })}
            />
            <label htmlFor="NQTCheckBox" className="checkbox-label">Suitable for NQTs (ECTs)</label>
          </div>
          { isSupply && <AgeRange /> }
        </div>
      </div>
    </div>
  ];

  const quickapplyElm = (<div data-tip data-for="quickapply" className="margin-top-sm pull-left quick-apply-filter">
    <Checkbox
      id="quickApplyCheckBox"
      caption="toggle"
      checked={quickApplyValue}
      onChange={(e) => changeQuickFilterValue(e.target.checked, { facets, keywords, base, push })}
    />
    <label htmlFor="quickApplyCheckBox" className="checkbox-label">Quick Apply jobs only</label>
    <ReactTooltip id="quickapply" place="bottom" type="dark" effect="solid">
      <span>Apply faster using your Tes CV, which saves <br /> your details for further applications.</span>
    </ReactTooltip>
  </div>);

  const searchShowMore = (<div className="show-more-filters-section">
    {quickapplyElm}
    {showMore}
  </div>);

  const filtersConfig = {
    search: {
      desktop: [
        keywordGroup,
        hr,
        threeFacets,
        searchShowMore
      ],
      mobile: [
        keywordGroup
      ]
    },
    embed: {
      desktop: [
        keywordGroup,
        hr,
        threeFacets,
        showMore,
        hr
      ],
      mobile: [
        keywordGroup
      ]
    },
    supply: {
      desktop: [
        supplyHead,
        keywordGroup,
        hr,
        showMore,
        hr
      ],
      mobile: [
        supplyHead,
        keywordGroup
      ]
    }
  };

  return (
    <div className={`top-filters ${isSupply ? 'supply-search' : ''} ${isEmbed ? 'embed-search' : ''}`}>
      <form
        onSubmit={evt => checkSubmit(evt, push, base, keywords)}
        data-module="search-section"
        data-id="search-jobs"
        id="search-jobs"
        className="clearfix top-filters-form"
      >
        {filtersConfig[searchType || 'search'][isMobile ? 'mobile' : 'desktop']}
      </form>
    </div>
  );
};

TopFilters.propTypes = {
  pathname: PropTypes.string.isRequired,
  keywords: PropTypes.string.isRequired,
  facets: PropTypes.object.isRequired,
  clearFilters: PropTypes.func,
  toggleShowMoreVisible: PropTypes.func,
  changeSelectedMoreFilter: PropTypes.func,
  selectedFacets: PropTypes.array,
  searchType: PropTypes.string,
  employerId: PropTypes.string,
  loading: PropTypes.bool,
  isMobile: PropTypes.bool,
  locationBreadcrumb: PropTypes.array,
  showMoreVisible: PropTypes.bool,
  experiments: PropTypes.object,
  metadata: PropTypes.object,
  history: PropTypes.shape({
    listen: PropTypes.func.isRequired
  }).isRequired
};

TopFilters.defaultProps = {
  metadata: {
    count: 0
  }
};

export default withRouter(TopFilters);
